import Label from "components/Label/Label";
import { FC, useEffect, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import CommonLayout from "./CommonLayout";
import { Helmet } from "react-helmet-async";
import { avatarImgs } from "contains/fakeData";
import { useTranslation } from "react-i18next";
import {
  doc,
  serverTimestamp,
  setDoc,
  getDoc
} from "firebase/firestore";
import { auth, storage, updateProfile, db } from "../../firebase";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { setCredentials } from "slices/authSlice";
import { UserProfile } from "types/userProfile";
import { checkObject } from "utils/validation";
export interface AccountPageProps {
  className?: string;
}

const AccountPage: FC<AccountPageProps> = ({ className = "" }) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>();
  const { t } = useTranslation();
  const { userInfo } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<UserProfile>({
    displayName: userInfo.displayName,
    email: userInfo.email,
    phoneNumber: userInfo.phoneNumber,
    birthDate: userInfo.birthDate,
    gender: userInfo.gender
  });
  const dispatch = useDispatch();

  const docRef = doc(db, "users", userInfo.uid);
  const fetchData = async () => {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const userDoc = docSnap.data();
      setData({
        displayName: userDoc.displayName,
        email: userDoc.email,
        phoneNumber: userDoc.phoneNumber,
        birthDate: userDoc.birthDate,
        gender: userDoc.gender
      });
    } else {
      console.log("No such document!");
    }
  }
  useEffect(() => {
    fetchData();
  });

  const handleFile = (file: File) => {
    if (file) {
      if (file.size > 200000) {
        toast.error(t('profilePhotoSizeError'), { position: "top-center", id: "nc-product-notify", duration: 3000 });
      } else {
        setFile(file);
      }
    }
  };

  const handleInput = (e: any) => {
    const id = e.target?.id;
    const value = e.target?.value!;
    setData({ ...data, [id]: value });
  };


  useEffect(() => {
    const uploadFile = () => {
      const storageRef = ref(storage, `user-profile-images/${userInfo.uid}`);
      const uploadTask = uploadBytesResumable(storageRef, file!);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          switch (snapshot.state) {
            case "paused":
              console.log("Upload is paused");
              break;
            case "running":
              console.log("Upload is running");
              break;
            default:
              break;
          }
        },
        (error) => {
          console.log(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            updateProfile(auth.currentUser!, {
              photoURL: downloadURL,
            }).then(() => {
              toast.success(t('profilePhotoUpdated'), { position: "top-center", id: "nc-product-notify", duration: 3000 });
              dispatch(setCredentials(auth.currentUser!));
            }).catch((error) => {
              console.log(error);
            });
            console.log("File available at", downloadURL);
          });
        }
      );
    };
    if (file) {
      uploadFile();
    }
  }, [file]);

  function getProfilePhoto() {
    if (file) {
      return URL.createObjectURL(file);
    } else {
      return userInfo.photoURL ? userInfo.photoURL : avatarImgs[10];
    }
  }

  async function handleUpdateProfile() {
    try {
      checkObject(data, []);
      setLoading(true);
      await setDoc(doc(db, "users", userInfo.uid), {
        ...data,
        timeStamp: serverTimestamp(),
      }).then(() => {
        toast.success(t('profileUpdated'), { position: "top-center", id: "nc-product-notify", duration: 3000 });
        dispatch(setCredentials(auth.currentUser!));
        setLoading(false);
      })
        .catch((error) => {
          console.log(error);
        });
    } catch (error: any) {
      toast.error(t('mustBeEntered').replace('#', t(error.message)),
        { position: "top-center", id: "nc-product-notify", duration: 5000 });
      setLoading(false);
    }
  }

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>{t('myAccount')} | {process.env.REACT_APP_NAME}</title>
      </Helmet>
      <CommonLayout>
        <div className="space-y-10 sm:space-y-12">
          {/* HEADING */}
          <h2 className="text-2xl sm:text-3xl font-semibold">
            {t('accountInformation')}
          </h2>
          <div className="flex flex-col md:flex-row">
            <div className="flex-shrink-0 flex items-start">
              {/* AVATAR */}
              <div className="relative rounded-full overflow-hidden flex">
                <img
                  src={getProfilePhoto()}
                  alt=""
                  className="w-32 h-32 rounded-full object-cover z-0"
                />
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer">
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <span className="mt-1 text-xs">{t('updateProfilePhoto')}</span>
                </div>
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  id="file"
                  onChange={(e) => handleFile(e.target.files![0])}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </div>
            </div>
            <div className="flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6">
              <div>
                <Label>{t('fullName')}</Label>
                <Input className="mt-1.5" id="displayName" value={data.displayName} onChange={handleInput} />
              </div>
              <div>
                <Label>{t('email')}</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-envelope"></i>
                  </span>
                  <Input
                    id="email"
                    className="!rounded-l-none"
                    placeholder="example@email.com"
                    value={data.email}
                    onChange={handleInput}
                  />
                </div>
              </div>

              {/* ---- */}
              <div className="max-w-lg">
                <Label>{t('birthDate')}</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-calendar"></i>
                  </span>
                  <Input
                    id="birthDate"
                    onChange={handleInput}
                    value={data.birthDate ?? ''}
                    className="!rounded-l-none"
                    type="date"
                  />
                </div>
              </div>
              {/* ---- */}
              <div>
                <Label>{t('gender')}</Label>
                <Select className="mt-1.5" id="gender" value={data.gender ?? ''} onChange={handleInput} >
                  <option value={t('male')}>{t('male')}</option>
                  <option value={t('female')}>{t('female')}</option>
                </Select>
              </div>

              {/* ---- */}
              <div>
                <Label>{t('phoneNumber')}</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-phone-volume"></i>
                  </span>
                  <Input
                    id="phoneNumber"
                    value={data.phoneNumber ?? ''}
                    onChange={handleInput}
                    className="!rounded-l-none"
                    placeholder="+90 123 456 78 90"
                  />
                </div>
              </div>
              <div className="pt-2">
                <ButtonPrimary loading={loading} onClick={() => handleUpdateProfile()} >
                  {t('update')}
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountPage;
